import { Loader2, Plus, Sparkles, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import type { AssistantBrandedImageGenerationPreviewReference } from "@/lib/assistant-branded-image-generation-previews";
import { resolveChatGeneratedMediaDimensionsAspectRatio } from "@/lib/chat-generated-media-aspect-ratio";
import { trpc } from "@/lib/trpc";
import { Badge, Button } from "@runcheetah/ui";
import type { ChatGeneratedImage } from "./assistant-chat-media-gallery-types";

type ProposalState =
  | { status: "active" }
  | { status: "cancelled" }
  | { status: "created"; batchId: string; runsRequired?: number; variantCount?: number }
  | {
      status: "failed";
      batchId: string;
      runsRequired?: number;
      variantCount?: number;
      message?: string;
    }
  | { status: "completed"; batchId?: string; runsRequired?: number; variantCount?: number };

const ACTIVE_PROPOSAL_STATE: ProposalState = { status: "active" };
const PENDING_CHAT_ID = "00000000-0000-4000-8000-000000000000";

export function AssistantBrandedImageGenerationPreviews({
  brandId,
  chatId,
  references,
  isStale = false,
  upgradeLabel,
  onUpgradeRequired,
  onUseImageInChat,
}: {
  brandId: string;
  chatId: string | null;
  references: AssistantBrandedImageGenerationPreviewReference[];
  isStale?: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  onUseImageInChat?: (image: ChatGeneratedImage) => void;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-3">
      {references.map((reference) => (
        <AssistantBrandedImageGenerationPreviewBanner
          key={reference.id}
          brandId={brandId}
          chatId={chatId}
          reference={reference}
          isStale={isStale}
          upgradeLabel={upgradeLabel}
          onUpgradeRequired={onUpgradeRequired}
          onUseImageInChat={onUseImageInChat}
        />
      ))}
    </div>
  );
}

function AssistantBrandedImageGenerationPreviewBanner({
  brandId,
  chatId,
  reference,
  isStale,
  upgradeLabel,
  onUpgradeRequired,
  onUseImageInChat,
}: {
  brandId: string;
  chatId: string | null;
  reference: AssistantBrandedImageGenerationPreviewReference;
  isStale: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  onUseImageInChat?: (image: ChatGeneratedImage) => void;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const proposalStorageKey = getProposalStorageKey(brandId, reference.id);
  const [proposalState, setProposalState] = useState(() => loadProposalState(proposalStorageKey));
  const [error, setError] = useState<string | null>(null);
  const requestedVariantCount = reference.request.variantCount;
  const cancelled = proposalState.status === "cancelled";
  const completed = proposalState.status === "completed";
  const failed = proposalState.status === "failed";
  const createdBatchId = proposalState.status === "created" ? proposalState.batchId : null;
  const completedBatchId =
    proposalState.status === "completed" ? (proposalState.batchId ?? null) : null;
  const activeBatchId = createdBatchId ?? completedBatchId;
  const closed = proposalState.status === "active" && isStale;
  const persistedRunsRequired =
    proposalState.status === "created" ||
    proposalState.status === "completed" ||
    proposalState.status === "failed"
      ? (proposalState.runsRequired ?? null)
      : null;
  const displayedVariantCount =
    proposalState.status === "created" ||
    proposalState.status === "completed" ||
    proposalState.status === "failed"
      ? (proposalState.variantCount ?? requestedVariantCount)
      : requestedVariantCount;
  const quoteQuery = trpc.brandAssistantChat.quoteCreateImageBatch.useQuery(
    { brandId, variantCount: requestedVariantCount },
    { enabled: !closed && !cancelled && !createdBatchId, retry: false },
  );
  const generatedImagesQuery = trpc.brandAssistantChat.generatedImages.useQuery(
    { brandId, chatId: chatId ?? PENDING_CHAT_ID },
    { enabled: Boolean(chatId && activeBatchId), refetchInterval: createdBatchId ? 5_000 : false },
  );
  const createBatch = trpc.brandAssistantChat.createImageBatch.useMutation({
    onSuccess: async (data) => {
      const runsRequired = quoteQuery.data?.runsRequired;
      const nextState = {
        status: "created" as const,
        batchId: data.batchId,
        variantCount: requestedVariantCount,
        ...(typeof runsRequired === "number" ? { runsRequired } : {}),
      };
      saveProposalState(proposalStorageKey, nextState);
      setProposalState(nextState);
      setError(null);
      await Promise.all([
        chatId
          ? utils.brandAssistantChat.generatedImages.invalidate({ brandId, chatId })
          : Promise.resolve(),
        utils.brand.getBillingOverview.invalidate({ brandId }),
      ]);
      toast.success(
        intl.formatMessage({
          id: "assistantImageGenerationPreview.createSuccess",
          defaultMessage: "Image generation queued",
        }),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const quote = quoteQuery.data ?? null;
  const visibleError =
    error ?? (failed ? proposalState.message : null) ?? quoteQuery.error?.message ?? null;
  const hasEnoughRuns =
    quote?.runsRemaining === null ||
    quote?.runsRequired === undefined ||
    quote.runsRemaining >= quote.runsRequired;
  const canConfirm = Boolean(
    chatId && quote && hasEnoughRuns && !closed && !cancelled && !completed && !createdBatchId,
  );

  useEffect(() => {
    if (!createdBatchId) {
      return;
    }

    const batch = generatedImagesQuery.data?.batches.find((item) => item.id === createdBatchId);
    if (!batch) {
      return;
    }

    if (batch.status === "failed") {
      const nextState = {
        status: "failed" as const,
        batchId: createdBatchId,
        variantCount: displayedVariantCount,
        ...(typeof persistedRunsRequired === "number"
          ? { runsRequired: persistedRunsRequired }
          : {}),
        ...(batch.lastError ? { message: batch.lastError } : {}),
      };
      saveProposalState(proposalStorageKey, nextState);
      setProposalState(nextState);
      setError(
        batch.lastError ||
          intl.formatMessage({
            id: "assistantImageGenerationPreview.failedMessage",
            defaultMessage: "Image generation failed. You can try again.",
          }),
      );
      return;
    }

    if (batch.status !== "generated") {
      return;
    }

    const nextState = {
      status: "completed" as const,
      batchId: createdBatchId,
      variantCount: displayedVariantCount,
      ...(typeof persistedRunsRequired === "number" ? { runsRequired: persistedRunsRequired } : {}),
    };
    saveProposalState(proposalStorageKey, nextState);
    setProposalState(nextState);
  }, [
    createdBatchId,
    displayedVariantCount,
    generatedImagesQuery.data?.batches,
    intl,
    persistedRunsRequired,
    proposalStorageKey,
  ]);

  const confirm = () => {
    if (!canConfirm || createBatch.isPending || !chatId) {
      return;
    }

    createBatch.mutate({
      brandId,
      chatId,
      ...reference.request,
      variantCount: requestedVariantCount,
    });
  };
  const generatedImages = activeBatchId
    ? (generatedImagesQuery.data?.images.filter((image) => image.batchId === activeBatchId) ?? [])
    : [];

  if (cancelled) {
    return (
      <div className="w-full rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        <FormattedMessage
          id="assistantImageGenerationPreview.cancelled"
          defaultMessage="Image generation proposal cancelled."
        />
      </div>
    );
  }

  return (
    <article className="w-full border-y border-border/60 bg-transparent">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <FormattedMessage
                id="assistantImageGenerationPreview.title"
                defaultMessage="Generate {count, plural, one {# Image} other {# Images}}?"
                values={{ count: displayedVariantCount }}
              />
            </div>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {reference.summary ?? reference.request.prompt}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant="outline"
              className="rounded-md border-border/70 bg-card/70 text-muted-foreground"
            >
              {reference.request.aspectRatio}
            </Badge>
          </div>
        </div>

        {!closed && !completed && !createdBatchId && !hasEnoughRuns && quote ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <FormattedMessage
              id="assistantImageGenerationPreview.insufficientRuns"
              defaultMessage="This needs {required} runs, but only {remaining} remain."
              values={{ required: quote.runsRequired, remaining: quote.runsRemaining ?? 0 }}
            />
          </div>
        ) : null}
        {!closed && !completed && !createdBatchId && visibleError ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {visibleError}
          </div>
        ) : null}

        {closed ? (
          <ProposalStatus>
            <X className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="font-medium text-foreground/58">
              <FormattedMessage
                id="assistantImageGenerationPreview.supersededLabel"
                defaultMessage="This proposal can no longer be accepted because the conversation has moved forward."
              />
            </span>
          </ProposalStatus>
        ) : createdBatchId ? (
          <ProposalStatus>
            <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="min-w-0 truncate bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.2s_linear_infinite]">
              <FormattedMessage
                id="assistantImageGenerationPreview.generatingImages"
                defaultMessage="{count, plural, one {Generating # Image} other {Generating # Images}}"
                values={{ count: displayedVariantCount }}
              />
            </span>
            {typeof persistedRunsRequired === "number" ? (
              <RunsUsed count={persistedRunsRequired} />
            ) : null}
          </ProposalStatus>
        ) : completed ? (
          <ProposalStatus>
            <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="min-w-0 text-muted-foreground">
              <FormattedMessage
                id="assistantImageGenerationPreview.generatedImages"
                defaultMessage="{count, plural, one {Generated # Image} other {Generated # Images}}"
                values={{ count: displayedVariantCount }}
              />
            </span>
            {typeof persistedRunsRequired === "number" ? (
              <RunsUsed count={persistedRunsRequired} />
            ) : null}
          </ProposalStatus>
        ) : (
          <div className="space-y-3 border-t border-border/60 pt-3">
            {failed ? (
              <div className="text-sm text-muted-foreground">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <X className="text-destructive/70" size="16" aria-hidden="true" />
                  <span className="font-medium text-destructive">
                    <FormattedMessage
                      id="assistantImageGenerationPreview.failedLabel"
                      defaultMessage="Generation failed"
                    />
                  </span>
                  {typeof persistedRunsRequired === "number" ? (
                    <RunsUsed count={persistedRunsRequired} />
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/65"
                disabled={createBatch.isPending}
                onClick={() => {
                  saveProposalState(proposalStorageKey, { status: "cancelled" });
                  setProposalState({ status: "cancelled" });
                }}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              {!hasEnoughRuns && onUpgradeRequired ? (
                <Button type="button" className="rounded-full" onClick={onUpgradeRequired}>
                  {upgradeLabel ?? (
                    <FormattedMessage id="runConfirmation.upgrade" defaultMessage="Upgrade" />
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-full"
                  disabled={!canConfirm || createBatch.isPending}
                  onClick={confirm}
                >
                  {createBatch.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {quote ? (
                    <FormattedMessage
                      id="assistantImageGenerationPreview.confirmWithRuns"
                      defaultMessage="Generate {count, plural, one {# Image} other {# Images}} for {runCount, plural, =0 {free} one {# run} other {# runs}}"
                      values={{ count: displayedVariantCount, runCount: quote.runsRequired }}
                    />
                  ) : (
                    <FormattedMessage
                      id="assistantImageGenerationPreview.confirm"
                      defaultMessage="Generate {count, plural, one {# Image} other {# Images}}"
                      values={{ count: displayedVariantCount }}
                    />
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
        {generatedImages.length > 0 ? (
          <GeneratedImageResultGrid images={generatedImages} onUseImageInChat={onUseImageInChat} />
        ) : null}
      </div>
    </article>
  );
}

function GeneratedImageResultGrid({
  images,
  onUseImageInChat,
}: {
  images: ChatGeneratedImage[];
  onUseImageInChat?: (image: ChatGeneratedImage) => void;
}) {
  return (
    <div className="border-t border-border/60 pt-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {images.map((image) => {
          const aspectRatio = resolveChatGeneratedMediaDimensionsAspectRatio(image);

          return (
            <div
              key={image.generatedAssetId}
              className="group relative overflow-hidden rounded-2xl bg-muted shadow-sm"
              style={{ aspectRatio }}
            >
              <img
                src={image.signedUrl}
                alt={image.prompt ?? "Generated image"}
                className="h-full w-full object-contain transition duration-300 group-hover:scale-[1.03]"
                loading="lazy"
              />
              {onUseImageInChat ? (
                <button
                  type="button"
                  className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-background/78 px-2.5 py-1.5 text-[11px] font-bold text-foreground opacity-0 shadow-lg shadow-black/15 backdrop-blur transition hover:bg-background group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
                  onClick={() => onUseImageInChat(image)}
                >
                  <Plus className="h-3.5 w-3.5 text-primary" aria-hidden />
                  <FormattedMessage
                    id="pulse.mediaGallery.addToTask"
                    defaultMessage="Add to task"
                  />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProposalStatus({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function RunsUsed({ count }: { count: number }) {
  return (
    <>
      <span className="h-1 w-1 rounded-full bg-muted-foreground/42" aria-hidden="true" />
      <span className="font-medium text-foreground/58">
        <FormattedMessage
          id="assistantImageGenerationPreview.runsUsed"
          defaultMessage="{count, plural, =0 {Free} one {# run used} other {# runs used}}"
          values={{ count }}
        />
      </span>
    </>
  );
}

function getProposalStorageKey(brandId: string, proposalId: string) {
  return `assistant-branded-image-generation:${brandId}:${proposalId}`;
}

function loadProposalState(key: string): ProposalState {
  if (typeof window === "undefined") {
    return ACTIVE_PROPOSAL_STATE;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return ACTIVE_PROPOSAL_STATE;
    }

    const parsed = JSON.parse(raw) as ProposalState;
    if (
      parsed.status === "active" ||
      parsed.status === "cancelled" ||
      parsed.status === "created" ||
      parsed.status === "failed" ||
      parsed.status === "completed"
    ) {
      return parsed;
    }
  } catch {
    return ACTIVE_PROPOSAL_STATE;
  }

  return ACTIVE_PROPOSAL_STATE;
}

function saveProposalState(key: string, state: ProposalState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Losing persisted proposal state should not block generation.
  }
}
